import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginCredentials, SignupData, Subscription, ApiResponse, DecodedToken } from '../types';
import { apiService, clearStoredAuthCredentials } from '../services/api';
import { getUserSubscription } from '../services/stripe';
import { jwtDecode } from 'jwt-decode';


interface AuthContextType {
  user: User | null;
  subscription: Subscription | null;
  isLoading: boolean;
  restaurantDetails: any | null;
  login: (credentials: LoginCredentials) => Promise<ApiResponse<{ status: boolean; token: string ,message: string }>>;
  signup: (signupData: SignupData) => Promise<ApiResponse<{ user: User; token: string }>>;
  logout: () => void;
  refreshSubscription: () => Promise<void>;
  skipSubscription: () => void;
  hasProPlan: () => boolean;
  hasBasicPlan: () => boolean;
  hasAnyPlan: () => boolean;
  loadRestaurantDetails: (forceRefresh?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Module-level flag that persists across component remounts (handles React Strict Mode)
let hasInitializedAuth = false;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [restaurantDetails, setRestaurantDetails] = useState<any | null>(null);

  useEffect(() => {
    localStorage.removeItem("tables");
    localStorage.removeItem("categories");
    localStorage.removeItem("productImages");
    localStorage.removeItem("feedbackImages");
    localStorage.removeItem("restaurantDetails");
  }, []); // Runs once on component mount (page load/reload)

  const loadRestaurantDetails = useCallback(async (forceRefresh: boolean = false) => {
    console.log('loadRestaurantDetails', forceRefresh);
    try {
      // If forceRefresh is true, skip localStorage and fetch from API directly
      if (!forceRefresh) {
        // First check if we already have restaurant details in localStorage
        const existingDetails = localStorage.getItem('restaurantDetails');
        if (existingDetails) {
          const details = JSON.parse(existingDetails);
          setRestaurantDetails(details);
          console.log('Restaurant details loaded from localStorage:', details);
          return;
        }
      }

      // Fetch from API (either because localStorage is empty or forceRefresh is true)
      const response = await apiService.getRestaurantProfile();
      if (response.status && response.doc) {
        const details = response.doc;
        setRestaurantDetails(details);
        
        // Save to localStorage for future use
        localStorage.setItem('restaurantDetails', JSON.stringify(details));
        console.log('Restaurant details fetched from API and saved to localStorage:', details);
      } else {
        console.error('Failed to fetch restaurant details:', response.message);
      }
    } catch (error) {
      console.error('Error loading restaurant details:', error);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    // Prevent duplicate initialization (React Strict Mode causes effects to run twice in dev)
    // Use module-level variable that persists across component remounts
    if (hasInitializedAuth) {
      console.log('Auth status already checked, skipping duplicate call');
      return;
    }
    hasInitializedAuth = true;

    const token = localStorage.getItem('auth_token');

    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);

        // Optional: check for required fields
        if (!decoded.data.userId || !decoded.data.mobile) {
          throw new Error('Invalid token payload');
        }
        // Set user data from token (including name)
        setUser({ 
          mobile: decoded.data.mobile,
          name: decoded.data.name
        });

        // Load restaurant details after user is set - always fetch fresh data on initial dashboard load
        await loadRestaurantDetails(true);

        // await loadSubscription(decoded.data.userId);

      } catch (err) {
        console.error('Token error:', err);
        clearStoredAuthCredentials();
      }
    }

    setIsLoading(false);
  }, [loadRestaurantDetails]);

  useEffect(() => {
    checkAuthStatus();
    
    // Cleanup function to reset flag on unmount (only needed if you want to allow re-initialization on remount)
    // For our use case, we want it to persist across remounts, so we don't reset it
    return () => {
      // Note: We intentionally don't reset hasInitializedAuth here
      // because we want it to persist across Strict Mode remounts
    };
  }, [checkAuthStatus]);

  const loadSubscription = async () => {
    const result = await getUserSubscription();
    if (result.success && result.data) {
      setSubscription(result.data);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<ApiResponse<{ status: boolean; token: string ,message: string }>> => {
    const result = await apiService.login(credentials);
    if (result.status) {
      // After successful login, decode the token and set user data
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const decoded = jwtDecode<DecodedToken>(token);
          setUser({ 
            mobile: decoded.data.mobile,
            name: decoded.data.name
          });
          
          // Load restaurant details after successful login - force refresh to get latest plan status
          await loadRestaurantDetails(true);
        } catch (err) {
          console.error('Token decode error:', err);
        }
      }
    }
    return {status:result.status,token:result.token, message:result.message};
  };

  const signup = async (signupData: SignupData): Promise<ApiResponse<{ user: User; token: string }>> => {
    const result = await apiService.signup(signupData);
    if (result.status) {
      // After successful signup, decode the token and set user data
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const decoded = jwtDecode<DecodedToken>(token);
          setUser({ 
            mobile: decoded.data.mobile,
            name: decoded.data.name
          });
          
          // Load restaurant details after successful signup
          await loadRestaurantDetails();
        } catch (err) {
          console.error('Token decode error:', err);
        }
      }
    }
    return result;
  };

  const logout = () => {
    setUser(null);
    setSubscription(null);
    setRestaurantDetails(null);
    apiService.logout();
    clearStoredAuthCredentials();
    localStorage.removeItem('subscription_skipped');
    localStorage.removeItem('restaurantDetails');
    localStorage.removeItem('orderStatusFilter');
    localStorage.removeItem('orderTableFilter');
  };

  const refreshSubscription = async () => {
    if (user) {
      await loadSubscription();
    }
  };

  const skipSubscription = () => {
    localStorage.setItem('subscription_skipped', 'true');
    // Create a mock subscription to indicate skipped status
    setSubscription({
      userId: user?._id || '',
      customerId: 'temp',
      status: 'not_started'
    });
  };

  const normalizePlan = (plan: unknown): string =>
    typeof plan === 'string' ? plan.trim().toLowerCase() : '';

  const hasProPlan = useCallback(() => {
    const subscriptionUnlocksPro =
      subscription != null &&
      (subscription.status === 'active' || subscription.status === 'trialing');

    if (subscriptionUnlocksPro) {
      return true;
    }

    if (restaurantDetails && normalizePlan(restaurantDetails.plan) === 'pro') {
      return true;
    }

    try {
      const storedDetails = localStorage.getItem('restaurantDetails');
      if (storedDetails) {
        const details = JSON.parse(storedDetails);
        if (normalizePlan(details.plan) === 'pro') {
          return true;
        }
      }
    } catch (error) {
      console.error('Error parsing restaurant details from localStorage:', error);
    }

    return false;
  }, [restaurantDetails, subscription]);

  const hasBasicPlan = useCallback(() => {
    if (hasProPlan()) {
      return false;
    }

    if (restaurantDetails && normalizePlan(restaurantDetails.plan) === 'basic') {
      return true;
    }

    try {
      const storedDetails = localStorage.getItem('restaurantDetails');
      if (storedDetails) {
        const details = JSON.parse(storedDetails);
        if (normalizePlan(details.plan) === 'basic') {
          return true;
        }
      }
    } catch (error) {
      console.error('Error parsing restaurant details from localStorage:', error);
    }

    return false;
  }, [restaurantDetails, hasProPlan]);

  const hasAnyPlan = useCallback(() => {
    return hasProPlan() || hasBasicPlan();
  }, [hasProPlan, hasBasicPlan]);

  const value: AuthContextType = {
    user,
    subscription,
    isLoading,
    restaurantDetails,
    login,
    signup,
    logout,
    refreshSubscription,
    skipSubscription,
    hasProPlan,
    hasBasicPlan,
    hasAnyPlan,
    loadRestaurantDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};